<script lang="ts" setup generic="T">
	import { cn } from '~/utils/functions'
	import { Splide, SplideSlide, type SplideOptions } from '@splidejs/vue-splide'
	import '@splidejs/vue-splide/css/core'
	import type { ClassNameValue } from 'tailwind-merge'

	const {
		indicatorPos = 'bottom',
		indicator = { on: true },
		defaultIndex = 0
	} = defineProps<{
		carouselList: T[]
		/**
		 * @default 'top'
		 * */
		indicatorPos?: 'top' | 'bottom'
		listCls?: ClassNameValue
		indicator?: {
			activeCls?: string
			class?: string
			on?: boolean
		}
		options?: SplideOptions
		defaultIndex?: number
	}>()
	defineOptions({
		inheritAttrs: false
	})
	const slideRef = useTemplateRef('splideRef')
	const indicatorRef = useTemplateRef('indicator')
	const attr = useAttrs()
	const cls = String(attr?.class || '')
	const { isMobile } = useMobile()
	const currentIndex = shallowRef(defaultIndex)

	const moveSplide = (index: number) => (slideRef.value && slideRef.value?.go && slideRef.value.go(index)) || null

	defineExpose({
		moveSplide,
		slideRef,
		indicatorRef
	})
	function onArrowsMounted(splide: any, prev: any, next: any) {
		return null
	}

	onMounted(() => {
		if (slideRef.value) {
			const splideInstance = slideRef.value?.splide?.event
			if (splideInstance) {
				splideInstance.on('moved', e => {
					if (!isNaN(e)) {
						currentIndex.value = e
					}
				})
			}
		}
	})
</script>

<template>
	<div :class="cn('flex gap-y-[11px]', indicatorPos === 'bottom' ? 'flex-col-reverse' : 'flex-col', cls)">
		<client-only>
			<tools-carousel-dots
				v-if="indicator?.on"
				ref="indicator"
				v-model="currentIndex"
				:active-cls="['w-[28px] bg-[#3BAA90]', indicator.activeCls]"
				:dot-class="['h-[6px] w-[6px] bg-[#E2F7F1]', indicator?.class ?? '']"
				:count="carouselList.length"
				@change="moveSplide"
			/>

			<splide
				:class="
					cn(
						'w-full mx-auto justify-center carousel relative flex items-center xl:justify-center overflow-hidden overflow-x-auto',
						listCls
					)
				"
				ref="splideRef"
				:options="{
					perPage: 1,
					start: currentIndex,
					autoWidth: true,
					gap: '20px',
					padding: isMobile ? '2%' : '0',
					type: isMobile ? 'loop' : 'slide',
					focus: 'center',
					arrows: false,
					pagination: false,
					drag: true,
					lazyLoad: 'sequential',
					...(options || {})
				}"
				@splide:arrows:mounted="onArrowsMounted"
			>
				<splide-slide v-for="(item, index) in carouselList" :key="index">
					<slot :item="item" :index="index" />
				</splide-slide>
			</splide>

			<template #fallback>
				<div
					:class="
						cn(
							'w-full mx-auto justify-center carousel relative flex items-center xl:justify-center overflow-hidden overflow-x-auto',
							listCls
						)
					"
				>
					<div
						v-for="(item, index) in carouselList"
						:key="`ssr-${index}`"
						class="flex-none px-[10px]"
						:style="{ width: 'auto' }"
					>
						<slot :item="item" :index="index" />
					</div>
				</div>
			</template>
		</client-only>
	</div>
</template>

<style></style>
